import React, { useMemo, useState, useEffect } from 'react';
import { type Asset } from 'types/asset.types';
import {
  PanelTitle,
  EmptyState,
  Badge,
  AssetGrid,
  ControlRoomMainGrid,
  PaginationContainer,
  PaginationIndicator,
  PaginationButton,
} from '../styles';
import { handleAssetStatus } from 'views/Settings/ExternalUsers/AssetView/helpers/common';
import { useTranslation } from 'react-i18next';

interface ControlRoomAssetGridProps {
  haulers: Asset[];
  loaders: Asset[];
  haulersOnlineCount: number;
  loadersOnlineCount: number;
  renderLeftAsset: (asset: Asset) => React.ReactNode;
  renderRightAsset: (asset: Asset) => React.ReactNode;
  enableDarkTheme?: boolean;
  latestAvailableData?: any;
  leftTitle?: string;
  rightTitle?: string;
  itemsPerPage?: number;
  resetPage?: any;
}

export const ControlRoomAssetGrid: React.FC<ControlRoomAssetGridProps> = ({
  haulers,
  loaders,
  haulersOnlineCount,
  loadersOnlineCount,
  renderLeftAsset,
  renderRightAsset,
  enableDarkTheme,
  latestAvailableData = {},
  leftTitle,
  rightTitle,
  itemsPerPage = 8,
  resetPage,
}) => {
  const { t } = useTranslation();
  const resolvedLeftTitle = leftTitle ?? t('control_room_haulers');
  const resolvedRightTitle = rightTitle ?? t('control_room_loaders');
  const getSortPriority = (asset: Asset) => {
    const isAvailable = handleAssetStatus(asset.isActive) === 'available';
    const deviceId = asset.device?.deviceId;
    const stats = deviceId ? latestAvailableData[deviceId] : null;
    const ignitionOn = stats?.ignitionOn === true;

    if (isAvailable && ignitionOn) return 1;
    if (isAvailable && !ignitionOn) return 2;
    if (!isAvailable && ignitionOn) return 3;
    return 4;
  };

  const getUtilization = (asset: Asset) => {
    const deviceId = asset.device?.deviceId || '';
    const stats = latestAvailableData[deviceId] || {};
    return parseFloat(stats.utilization || 0);
  };

  const sortedHaulers = useMemo(() => {
    return [...haulers].sort((a, b) => {
      const pA = getSortPriority(a);
      const pB = getSortPriority(b);
      if (pA !== pB) return pA - pB;
      return getUtilization(b) - getUtilization(a);
    });
  }, [haulers, latestAvailableData]);

  const sortedLoaders = useMemo(() => {
    return [...loaders].sort((a, b) => {
      const pA = getSortPriority(a);
      const pB = getSortPriority(b);
      if (pA !== pB) return pA - pB;
      return getUtilization(b) - getUtilization(a);
    });
  }, [loaders, latestAvailableData]);

  const [pageIndex, setPageIndex] = useState(0);
  const ITEMS_PER_PAGE = itemsPerPage;

  const maxPages = useMemo(() => {
    const hPages = Math.ceil(haulers.length / ITEMS_PER_PAGE);
    const lPages = Math.ceil(loaders.length / ITEMS_PER_PAGE);
    return Math.max(hPages, lPages, 1);
  }, [haulers.length, loaders.length]);

  // Reset page if shift filtering reduces asset count
  useEffect(() => {
    if (pageIndex >= maxPages) {
      setPageIndex(0);
    }
  }, [maxPages, pageIndex]);

  // Reset page on external signal (e.g. carousel switch)
  useEffect(() => {
    setPageIndex(0);
  }, [resetPage]);

  // Disable pagination - show all assets
  const pagedHaulers = useMemo(() => {
    return sortedHaulers;
  }, [sortedHaulers]);

  const pagedLoaders = useMemo(() => {
    return sortedLoaders;
  }, [sortedLoaders]);

  return (
    <>
      <div
        style={{
          fontSize: 12,
          opacity: 0.5,
          marginBottom: 12,
          letterSpacing: '0.01em',
        }}
      >
        <span style={{ color: '#a78bfa', fontSize: 15, fontWeight: 600 }}>
          *
        </span>{' '}
        {t('control_room_algorithm_footnote')}
      </div>

      <ControlRoomMainGrid>
        {/* ── HAULERS COLUMN ── */}
        <div>
          <PanelTitle
            $enableDarkTheme={enableDarkTheme}
            style={{
              marginBottom: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>
              {resolvedLeftTitle} — {haulers.length} {t('std_asset_haulers')}
            </span>
            <span
              style={{
                color: 'rgba(255,255,255,0.15)',
                display: 'inline-block',
              }}
            >
              •
            </span>
            <Badge
              $variant={haulersOnlineCount > 0 ? 'success' : 'error'}
              $size="large"
              $enableDarkTheme={enableDarkTheme}
            >
              {haulersOnlineCount === haulers.length
                ? t('control_room_all_online')
                : haulersOnlineCount === 0
                ? t('control_room_all_offline')
                : t('control_room_count_online', { count: haulersOnlineCount })}
            </Badge>
          </PanelTitle>

          <div style={{ height: 16 }} />

          {haulers.length === 0 ? (
            <EmptyState $enableDarkTheme={enableDarkTheme}>
              {t('no_alerts_found')}
            </EmptyState>
          ) : (
            <AssetGrid key={pageIndex}>
              {pagedHaulers.map(renderLeftAsset)}
            </AssetGrid>
          )}
        </div>

        {/* ── DIVIDER ── */}
        <div
          style={{
            background: enableDarkTheme
              ? 'rgba(255, 255, 255, 0.12)'
              : 'rgba(0, 0, 0, 0.12)',
            width: 1,
            height: '100%',
          }}
        />

        {/* ── LOADERS COLUMN ── */}
        <div>
          <PanelTitle
            $enableDarkTheme={enableDarkTheme}
            style={{
              marginBottom: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>
              {resolvedRightTitle} — {loaders.length} {t('std_asset_loaders')}
            </span>
            <span
              style={{
                color: 'rgba(255,255,255,0.15)',
                display: 'inline-block',
              }}
            >
              •
            </span>
            <Badge
              $variant={loadersOnlineCount > 0 ? 'success' : 'error'}
              $size="large"
              $enableDarkTheme={enableDarkTheme}
            >
              {loadersOnlineCount === loaders.length
                ? t('control_room_all_online')
                : loadersOnlineCount === 0
                ? t('control_room_all_offline')
                : t('control_room_count_online', { count: loadersOnlineCount })}
            </Badge>
          </PanelTitle>

          <div style={{ height: 16 }} />

          {loaders.length === 0 ? (
            <EmptyState $enableDarkTheme={enableDarkTheme}>
              {t('no_alerts_found')}
            </EmptyState>
          ) : (
            <AssetGrid key={pageIndex}>
              {pagedLoaders.map(renderRightAsset)}
            </AssetGrid>
          )}
        </div>
      </ControlRoomMainGrid>
    </>
  );
};
